import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAromas, useFlavors } from "@/hooks/useReferenceData";
import { toUserFacingErrorMessage } from "@/lib/errors";
import { useAppLocale } from "@/lib/i18n";
import { m } from "@/paraglide/messages.js";

type NamedReferenceMutation = (args: any) => Promise<unknown>;

type NamedReferenceManagementDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  items: Array<{ _id: string; name?: string | null }> | undefined;
  createItem: NamedReferenceMutation;
  updateItem: NamedReferenceMutation;
  deleteItem: NamedReferenceMutation;
  title: string;
  description: string;
  editLabel: string;
  addLabel: string;
  namePlaceholder: string;
  searchPlaceholder: string;
  emptyText: string;
  createdMessage: string;
  updatedMessage: string;
  deletedMessage: string;
  saveErrorMessage: string;
  deleteErrorMessage: string;
  deleteConfirmTitle: string;
  deleteConfirmDescription: (name: string) => string;
  deleteConfirmAction: string;
  deleteConfirmCancel: string;
};

function NamedReferenceManagementDialog({
  open,
  onOpenChange,
  items,
  createItem,
  updateItem,
  deleteItem,
  title,
  description,
  editLabel,
  addLabel,
  namePlaceholder,
  searchPlaceholder,
  emptyText,
  createdMessage,
  updatedMessage,
  deletedMessage,
  saveErrorMessage,
  deleteErrorMessage,
  deleteConfirmTitle,
  deleteConfirmDescription,
  deleteConfirmAction,
  deleteConfirmCancel,
}: NamedReferenceManagementDialogProps) {
  useAppLocale();
  const [search, setSearch] = React.useState("");
  const [name, setName] = React.useState("");
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);

  const filtered = React.useMemo(() => {
    if (!items) return [];
    if (!search) return items;
    const query = search.toLowerCase();
    return items.filter((item) => item.name?.toLowerCase().includes(query));
  }, [items, search]);

  const resetForm = React.useCallback(() => {
    setName("");
    setEditingId(null);
  }, []);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error(m.name_required_simple());
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateItem({ id: editingId as any, name: name.trim() });
        toast.success(updatedMessage);
      } else {
        await createItem({ name: name.trim() });
        toast.success(createdMessage);
      }
      resetForm();
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, saveErrorMessage, {
          unauthorizedMessage: m.inventory_products_unauthorized(),
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item: { _id: string; name?: string | null }) => {
    setEditingId(item._id);
    setName(item.name ?? "");
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteItem({ id: id as any });
      toast.success(deletedMessage);
      if (editingId === id) {
        resetForm();
      }
    } catch (error) {
      toast.error(
        toUserFacingErrorMessage(error, deleteErrorMessage, {
          unauthorizedMessage: m.inventory_products_unauthorized(),
        }),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div className="space-y-3 rounded-md border p-3">
            <div className="grid gap-2">
              <Label>{editingId ? editLabel : addLabel}</Label>
              <Input value={name} onChange={(event) => setName(event.target.value)} placeholder={namePlaceholder} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
                {saving ? m.saving() : editingId ? m.update() : m.add()}
              </Button>
              {editingId && (
                <Button size="sm" variant="outline" onClick={resetForm}>{m.cancel()}</Button>
              )}
            </div>
          </div>

          <Input placeholder={searchPlaceholder} value={search} onChange={(event) => setSearch(event.target.value)} />

          <div className="max-h-[300px] space-y-1 overflow-y-auto">
            {filtered.map((item) => (
              <div
                key={item._id}
                className="flex items-center justify-between rounded-md px-3 py-2 hover:bg-muted/50"
              >
                <p className="text-sm font-medium">{item.name}</p>
                <div className="flex gap-1">
                  <Button size="icon-xs" variant="ghost" onClick={() => handleEdit(item)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon-xs" variant="ghost" aria-label={`${deleteConfirmAction}: ${item.name ?? ""}`}>
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{deleteConfirmTitle}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {deleteConfirmDescription(item.name ?? "")}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{deleteConfirmCancel}</AlertDialogCancel>
                        <AlertDialogAction
                          variant="destructive"
                          onClick={() => void handleDelete(item._id)}
                        >
                          {deleteConfirmAction}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">{emptyText}</p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

type TastingNoteManagementDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function FlavorManagementDialog({ open, onOpenChange }: TastingNoteManagementDialogProps) {
  const { locale } = useAppLocale();
  const flavors = useFlavors();
  const createFlavor = useMutation(api.products.createFlavor);
  const updateFlavor = useMutation(api.products.updateFlavor);
  const deleteFlavor = useMutation(api.products.deleteFlavor);

  return (
    <NamedReferenceManagementDialog
      open={open}
      onOpenChange={onOpenChange}
      items={flavors}
      createItem={createFlavor}
      updateItem={updateFlavor}
      deleteItem={deleteFlavor}
      title={locale === "es" ? "Administrar sabores" : "Manage Flavors"}
      description={locale === "es" ? "Agrega, edita o elimina notas de sabor." : "Add, edit, or remove flavor notes."}
      editLabel={locale === "es" ? "Editar sabor" : "Edit Flavor"}
      addLabel={locale === "es" ? "Agregar sabor" : "Add Flavor"}
      namePlaceholder={locale === "es" ? "Nombre del sabor" : "Flavor name"}
      searchPlaceholder={locale === "es" ? "Buscar sabores..." : "Search flavors..."}
      emptyText={locale === "es" ? "No se encontraron sabores." : "No flavors found."}
      createdMessage={locale === "es" ? "Sabor creado" : "Flavor created"}
      updatedMessage={locale === "es" ? "Sabor actualizado" : "Flavor updated"}
      deletedMessage={locale === "es" ? "Sabor eliminado" : "Flavor deleted"}
      saveErrorMessage="Failed to save flavor"
      deleteErrorMessage="Failed to delete flavor"
      deleteConfirmTitle={locale === "es" ? "¿Eliminar sabor?" : "Delete flavor?"}
      deleteConfirmDescription={(name) =>
        locale === "es"
          ? `Esto eliminará "${name}" de las notas de sabor. Los productos que lo referencien dejarán de mostrarlo. Esta acción no se puede deshacer.`
          : `This will remove "${name}" from the flavor notes. Products referencing it will no longer display it. This cannot be undone.`
      }
      deleteConfirmAction={locale === "es" ? "Eliminar sabor" : "Delete flavor"}
      deleteConfirmCancel={locale === "es" ? "Cancelar" : "Cancel"}
    />
  );
}

export function AromaManagementDialog({ open, onOpenChange }: TastingNoteManagementDialogProps) {
  const { locale } = useAppLocale();
  const aromas = useAromas();
  const createAroma = useMutation(api.products.createAroma);
  const updateAroma = useMutation(api.products.updateAroma);
  const deleteAroma = useMutation(api.products.deleteAroma);

  return (
    <NamedReferenceManagementDialog
      open={open}
      onOpenChange={onOpenChange}
      items={aromas}
      createItem={createAroma}
      updateItem={updateAroma}
      deleteItem={deleteAroma}
      title={locale === "es" ? "Administrar aromas" : "Manage Aromas"}
      description={locale === "es" ? "Agrega, edita o elimina notas de aroma." : "Add, edit, or remove aroma notes."}
      editLabel={locale === "es" ? "Editar aroma" : "Edit Aroma"}
      addLabel={locale === "es" ? "Agregar aroma" : "Add Aroma"}
      namePlaceholder={locale === "es" ? "Nombre del aroma" : "Aroma name"}
      searchPlaceholder={locale === "es" ? "Buscar aromas..." : "Search aromas..."}
      emptyText={locale === "es" ? "No se encontraron aromas." : "No aromas found."}
      createdMessage={locale === "es" ? "Aroma creado" : "Aroma created"}
      updatedMessage={locale === "es" ? "Aroma actualizado" : "Aroma updated"}
      deletedMessage={locale === "es" ? "Aroma eliminado" : "Aroma deleted"}
      saveErrorMessage="Failed to save aroma"
      deleteErrorMessage="Failed to delete aroma"
      deleteConfirmTitle={locale === "es" ? "¿Eliminar aroma?" : "Delete aroma?"}
      deleteConfirmDescription={(name) =>
        locale === "es"
          ? `Esto eliminará "${name}" de las notas de aroma. Los productos que lo referencien dejarán de mostrarlo. Esta acción no se puede deshacer.`
          : `This will remove "${name}" from the aroma notes. Products referencing it will no longer display it. This cannot be undone.`
      }
      deleteConfirmAction={locale === "es" ? "Eliminar aroma" : "Delete aroma"}
      deleteConfirmCancel={locale === "es" ? "Cancelar" : "Cancel"}
    />
  );
}
